/**
 * Route guard — 로그인 필요한 경로에 mount.
 *
 * 인증 안 된 경우:
 *  - `auth` SubActivity 를 현재 페이지 위에 오버레이로 띄움 (fullscreen)
 *  - `pendingAuthIntent` 에 원래 가려던 URL 을 저장 → 로그인 성공 시
 *    `LoginPage.applyTokens` 가 consume 하여 해당 URL 을 다시 열어줌
 *  - 로그인 취소 시 auth activity 가 `fallbackPath="/"` 로 닫혀 홈으로 복귀
 *    → 보호된 라우트가 unmount 되므로 reopen loop 없음
 *  - **브라우저 뒤로가기로 닫은 경우**는 URL 이 보호 경로에 남는다 → 아래 escape
 *    hatch 가 취소와 같은 결말(스택 비우고 홈)로 맞춘다
 *
 * 사용 예:
 *   <Route element={<RequireAuth><Layout /></RequireAuth>}>
 *     <Route path="/me" element={<MyPage />} />
 *   </Route>
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { useActivityStore } from "../../store/activityStore";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { setPendingAuthIntent, setAuthReturnPath } from "../../navigation/pendingAuthIntent";
import { useMainTabActive } from "./mainTabActiveContext";
import { AUTH_ACTIVITY_FALLBACK_PATH } from "./ActivityHost";
import type { ReactNode } from "react";

interface RequireAuthProps {
  children: ReactNode;
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const hasAuthActivity = useActivityStore((s) =>
    s.activities.some((a) => a.kind === "auth"),
  );
  const navigator = useActivityNavigator();
  const location = useLocation();
  // keep-alive 메인 탭 슬롯은 숨겨져도(display:none) mount 상태로 남는다. 숨은 MySlot 의
  // RequireAuth 가 로그인창을 띄우면 **남의 화면 위에** 로그인이 올라온다 — 2026-09-15
  // 제보: 내 정보 → 뒤로가기 → 고객센터를 열면 로그인창. (/support 진입 순간 슬롯이
  // 잠깐 unmount→remount 되어 아래 openedRef 가 초기화되는 게 방아쇠였다.)
  const isVisible = useMainTabActive();
  // navigator/location 이 effect 재발화 트리거가 되므로, "이미 한 번 띄웠으면 다시 띄우지
  // 않는다" 가드. 인증되면 reset 되어 다시 mount 될 때 정상 동작.
  const openedRef = useRef(false);
  // openedRef 를 세운 시점의 보호 경로. keep-alive 로 숨어있는 슬롯(예: 홈에 있는데
  // mainTabRegistry 의 MySlot 이 mount 된 채 남은 경우)이 남의 로그인창 닫힘에
  // 반응해 화면을 홈으로 끌고 가지 않도록, 아래 escape hatch 를 이 경로에서만 연다.
  const openedForPathRef = useRef<string | null>(null);
  // 이 인스턴스가 "로그인창이 떠 있는 것"을 실제로 본 적이 있는가. 아래 escape hatch 는
  // 봤다가 사라진 경우에만 연다 — 첫 마운트에서는 open 이 microtask 라 같은 커밋의
  // hasAuthActivity 가 아직 false 다(이 구분이 없으면 로그인창 대신 홈으로 튕긴다).
  const sawAuthActivityRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      openedRef.current = false;
      return;
    }
    // 안 보이는 슬롯은 아무것도 하지 않는다 + "아직 안 띄웠다"로 되돌린다(다시 보일 때
    // 정상적으로 띄우기 위해).
    if (!isVisible) {
      openedRef.current = false;
      openedForPathRef.current = null;
      sawAuthActivityRef.current = false;
      return;
    }
    if (openedRef.current) return;
    // 이미 다른 RequireAuth (예: parent main-tab slot) 가 auth activity 를 띄웠으면 skip.
    // 뒤로가기 직후엔 아직 정리되지 않은 auth activity 가 여기 보일 수 있다 — 그래도
    // openedForPathRef 를 세워두므로 아래 escape hatch 가 이어받는다.
    if (
      useActivityStore.getState().activities.some((a) => a.kind === "auth")
    ) {
      openedRef.current = true;
      openedForPathRef.current = location.pathname;
      return;
    }
    openedRef.current = true;
    openedForPathRef.current = location.pathname;

    if (import.meta.env.DEV) {
      console.warn(
        `[RequireAuth] not authenticated → opening auth activity above ${location.pathname}`,
      );
    }

    const sourcePath = location.pathname + location.search;
    setPendingAuthIntent({
      type: "navigate",
      to: sourcePath,
      mode: "openActivity",
    });
    // OAuth roundtrip 후 callback 에서 활동 스택이 비어 fallback 정보를 잃는다.
    // sessionStorage 에 source path 를 따로 박아 OAuthCallbackPage 가 원래
    // 화면으로 돌아갈 수 있게 한다.
    setAuthReturnPath(sourcePath);

    // RequireAuth 가 SubActivity (예: me/settings) 안에서 wrapAuth 로 마운트된 경우,
    // 자식 effect 는 RootLayout 의 usePopstateSync effect 보다 먼저 실행되므로
    // 그 안에서 navigator.open 을 즉시 호출하면 store 변경 / location 변경 사이의
    // 타이밍 차이로 usePopstateSync 가 잘못된 reconcile 을 트리거 (auth 제거).
    // 부모 effect 들이 settle 한 다음 microtask 에 open 하도록 미룬다.
    // queueMicrotask 를 쓰는 이유: setTimeout 은 Strict Mode 의 mount→unmount→mount
    // 시뮬레이션 cleanup 에서 clearTimeout 으로 취소되어 fire 가 안 됨.
    queueMicrotask(() => {
      // 마이크로태스크 시점에 isAuthenticated 가 이미 true 면 (race), skip.
      if (useAuthStore.getState().accessToken) return;
      if (useActivityStore.getState().activities.some((a) => a.kind === "auth")) return;
      navigator.open({
        kind: "auth",
        presentation: "fullscreen",
        appBarAction: "close",
        url: "/login",
        fallbackPath: AUTH_ACTIVITY_FALLBACK_PATH,
      });
    });
  }, [isAuthenticated, isVisible, navigator, location.pathname, location.search]);

  // Escape hatch — 로그인창이 닫혔는데 URL 은 아직 보호 경로인 상태.
  //
  // 브라우저 뒤로가기(데스크탑)·하드웨어 백은 auth activity 만 벗겨내고 그 아래
  // 히스토리 항목으로 돌아간다. 그 항목이 보호 경로면(`navigate("/me")` 처럼 호출자가
  // 경로를 push 한 경우) 위 effect 는 openedRef 가드에 걸려 로그인창을 다시 띄우지
  // 않고, 이 컴포넌트는 null 을 렌더한다 → 크롬만 남고 본문이 빈 화면(2026-09-15 제보).
  //
  // 다시 띄우면 로그인 ↔ 보호 경로를 오가며 뒤로가기가 영영 안 먹으므로(원래 가드가
  // 막던 loop), X 로 닫았을 때와 같은 결말로 맞춘다 — 스택을 비우고 홈으로.
  // (dispatchBack 의 auth close 케이스 (1) 과 동일한 동작이다.)
  useEffect(() => {
    if (isAuthenticated) {
      sawAuthActivityRef.current = false;
      return;
    }
    if (!isVisible) return;
    if (hasAuthActivity) {
      sawAuthActivityRef.current = true;
      return;
    }
    if (!sawAuthActivityRef.current) return;
    // 숨어있는 keep-alive 슬롯이 남의 로그인창 닫힘에 반응하지 않도록, 지금 화면이
    // 이 가드가 지키는 경로일 때만.
    if (openedForPathRef.current !== location.pathname) return;
    sawAuthActivityRef.current = false;
    navigator.closeAllTo(AUTH_ACTIVITY_FALLBACK_PATH, { replace: true });
  }, [isAuthenticated, hasAuthActivity, isVisible, navigator, location.pathname]);

  if (!isAuthenticated) {
    return null;
  }
  return <>{children}</>;
}
